import React, { useCallback } from 'react';
import DancersStepView from './DancersStepView';
import useDancersData from '../../hooks/useDancersData';
import { DancersStepContainerProps } from '../../types/dancers';

/**
 * Container component that manages state and data fetching for the Dancers Step
 * Updated to use the useDancersData hook for better separation of concerns
 */
const DancersStepContainer: React.FC<DancersStepContainerProps> = ({
  event,
  pricingSettings,
  criteriaSettings,
  settingsLoading = false,
  settingsError = null,
  registrationData,
  updateRegistrationData,
  onNext,
  onBack,
  isDarkMode,
  renderNavigation
}) => {
  // Use the dancers data hook
  const dancersData = useDancersData(
    registrationData,
    updateRegistrationData,
    pricingSettings,
    criteriaSettings
  );

  // Create a combined handler that processes dancers data and then navigates
  const handleCombinedNext = useCallback(() => {
    // First process dancers using the hook's handleNext
    const result = dancersData.handleNext();
    
    // If dancers were successfully processed, trigger the parent's onNext
    if (result) {
      console.log('Dancers processed successfully, navigating to next step');
      // Call the parent's onNext to navigate
      onNext();
    } else {
      console.log('Failed to process dancers, staying on current step');
      // If there was a validation error, the error will have been displayed by handleNext
    }
  }, [dancersData.handleNext, onNext]);

  // Handle settings loading and error states
  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (settingsError) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-100 text-red-800'
      }`}>
        <p>Error loading settings: {settingsError}</p>
      </div>
    );
  }

  return (
    <DancersStepView
      loading={dancersData.loading}
      error={dancersData.error}
      dancers={dancersData.dancers}
      filteredDancers={dancersData.filteredDancers}
      searchQuery={dancersData.searchQuery}
      setSearchQuery={dancersData.setSearchQuery}
      filteredSelectedDancers={dancersData.filteredSelectedDancers}
      selectedSearchQuery={dancersData.selectedSearchQuery}
      setSelectedSearchQuery={dancersData.setSelectedSearchQuery}
      selectedDancers={dancersData.selectedDancers}
      showWarning={dancersData.showWarning}
      warningDancer={dancersData.warningDancer}
      cancelDeselect={dancersData.cancelDeselect}
      showRemoveConfirmation={dancersData.showRemoveConfirmation}
      dancerToRemove={dancersData.dancerToRemove}
      confirmDancerRemoval={dancersData.confirmDancerRemoval}
      cancelDancerRemoval={dancersData.cancelDancerRemoval}
      workshopLevels={dancersData.workshopLevels}
      totalPrice={dancersData.totalPrice}
      ageDivisions={dancersData.ageDivisions}
      isDarkMode={isDarkMode}
      
      // Utility functions
      calculateAge={dancersData.calculateAge}
      getInitials={dancersData.getInitials}
      formatAgeRange={dancersData.formatAgeRange}
      formatCurrency={dancersData.formatCurrency}
      getSuggestedWorkshopLevel={dancersData.getSuggestedWorkshopLevel}
      isDancerSelected={dancersData.isDancerSelected}
      findAgeGroupInfo={dancersData.findAgeGroupInfo}
      getWorkshopLevel={dancersData.getWorkshopLevel}
      
      // Event handlers
      toggleDancerSelection={dancersData.toggleDancerSelection}
      updateDancerOptions={dancersData.updateDancerOptions}
      onNext={handleCombinedNext}
      onBack={onBack}
      renderNavigation={renderNavigation}
    />
  );
};

export default DancersStepContainer; 